import React from 'react';
import classnames from 'classnames/bind';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import APIError from 'types/api_error';

import { useAPI } from 'hooks/api/useAPI';
import { useAccount } from 'hooks/api/useAccount';
import { useApplications } from 'hooks/api/useApplications';
import { useAuthentication } from 'hooks/api/useAuthentication';
import { useCurrentOrganization } from 'hooks/api/useCurrentOrganization.ts';
import { useOrganizations } from 'hooks/api/useOrganizations';
import { useHasApplicationAccess } from 'hooks/useHasAccess';
import { useRegistrationParams } from 'hooks/useRegistrationParams';
import { useSyncRingAccountAttributes } from 'hooks/useSyncRingAccountAttributes';
import { useCurrentPlan } from 'organizations/hooks/useCurrentPlan';

import AppContainer from 'application/views/App/AppContainer';
import { Embed } from 'application/views/Embed/Embed';

import Logo from 'images/lumeo-logo.svg?react';
import { MCPFrontendTools } from 'components/CopilotChat/MCPFrontendTools';
import { Button } from 'components/Button/Button';
import { Card } from 'components/Card/Card';
import { EnvironmentBanner } from 'components/EnvironmentBanner/EnvironmentBanner.tsx';
import { Heading } from 'components/Heading/Heading';
import { Loader } from 'components/Loader/Loader';
import { RequireAuth } from 'components/RequireAuth/RequireAuth';
import { SuspensionBanner } from 'organizations/components/SuspensionBanner/SuspensionBanner';

import styles from './Root.module.scss';

const c = classnames.bind(styles);

type AppOrgErrorProps = {
  error: APIError;
};

function AppOrgError({ error }: AppOrgErrorProps) {
  return (
    <div className={c('error-screen', 'error-message')}>
      <Logo className={c('error-screen-logo')} />

      <div className="error-message-content">
        <Heading level="1">{error.message}</Heading>
      </div>
    </div>
  );
}

// Ring's nonce is valid for ~10 minutes server-side. The wider window here absorbs clock skew, so
// stale params stop routing to /ring/bind without ever discarding a nonce the server would accept.
const RING_NONCE_MAX_AGE_MS = 15 * 60 * 1000;

export function Root() {
  const organizations = useOrganizations();
  const applications = useApplications();
  const account = useAccount();
  const { data: currentOrganization } = useCurrentOrganization();
  const { isLoading: isPlanLoading, isRingPlan } = useCurrentPlan();
  const { getParams } = useRegistrationParams();
  const hasApplicationAccess = useHasApplicationAccess();

  const isLoading =
    organizations.isLoading || applications.isLoading || account.isLoading;
  const location = useLocation();

  const { setApplicationID } = useAPI();

  const { isAuthenticated, logout } = useAuthentication();

  // Sync account attributes with registration params for Ring users
  useSyncRingAccountAttributes();

  // Filter applications to only those the user has access to
  const accessibleApps = React.useMemo(() => {
    if (!applications.data || !organizations.data) {
      return [];
    }

    return applications.data.filter((app) => {
      const org = organizations.data.find((o) => o.id === app.organization_id);
      return org && hasApplicationAccess(app.id, org.id);
    });
  }, [applications.data, organizations.data, hasApplicationAccess]);

  // Determine the next application to load
  const nextAppId = React.useMemo(() => {
    if (!accessibleApps.length) {
      return;
    }

    const [firstApp] = accessibleApps;
    const lastUsedAppId = localStorage.getItem('console.applicationID');

    if (
      lastUsedAppId &&
      accessibleApps.some(({ id }) => id === lastUsedAppId)
    ) {
      return lastUsedAppId;
    }

    return firstApp.id;
  }, [accessibleApps]);

  React.useEffect(() => {
    if (!nextAppId) {
      return;
    }

    setApplicationID(nextAppId);
    localStorage.setItem('console.applicationID', nextAppId);
  }, [nextAppId, setApplicationID]);

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} />;
  }

  if (organizations.error || applications.error) {
    return (
      <AppOrgError
        error={
          (organizations.error || applications.error) as unknown as APIError
        }
      />
    );
  }

  if (isLoading || !organizations.data || !applications.data) {
    return <Loader text="Loading your workspace..." showLogo />;
  }

  if (!applications.data.length) {
    return <Navigate to="/welcome" />;
  }

  // User has applications but no access to any of them
  if (!accessibleApps.length) {
    return (
      <div className={c('error-screen')}>
        <Logo className={c('error-screen-logo')} />
        <Card>
          <div className="flex flex-col items-center gap-1 text-center">
            <Heading level="1">No Access</Heading>
            <p>
              You don't have access to any workspaces. Please contact your
              organization administrator to request access.
            </p>
            <Button variant="primary" onClick={logout}>
              Log out
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Check if Ring partner is trying to use an existing account
  // Only check after we know applications exist
  const registrationParams = getParams();
  // One-way Ring linking lands on /ring/link?nonce=&time=; an authenticated user runs no signup, so
  // the nonce reaches the server only via the bind below.
  const isOneWayRingLink = Boolean(
    registrationParams?.nonce &&
      registrationParams?.time &&
      Date.now() - Number(registrationParams.time) < RING_NONCE_MAX_AGE_MS
  );

  if (isOneWayRingLink) {
    return <Navigate to="/ring/bind" />;
  }

  if (registrationParams?.partner === 'ring') {
    if (isPlanLoading) {
      return <Loader text="Loading your workspace..." showLogo />;
    }

    if (!isRingPlan) {
      return <Navigate to="/ring-existing-account" />;
    }
  }

  // Check if Ring integration needs confirmation
  const ringLinkState =
    account.data?.account_attributes?.ring_link_state ||
    (registrationParams?.partner === 'ring' ? 'unlinked' : undefined);

  if (ringLinkState && ringLinkState !== 'linked') {
    return <Navigate to="/confirm-ring" />;
  }

  // If already linked but has Ring registration params, show re-link confirmation
  if (ringLinkState === 'linked' && registrationParams?.partner === 'ring') {
    return <Navigate to="/confirm-ring" />;
  }

  // Overwrite --banner-offset-y when suspension banner is active
  const isSuspended = Boolean(currentOrganization?.is_suspended);
  const offset = (import.meta.env.PROD ? 0 : 2) + (isSuspended ? 4.5 : 0);

  return (
    <div
      className={c('page-wrap', {
        '--banner-offset-y': isSuspended ? `${offset}rem` : false,
      })}
    >
      <EnvironmentBanner />

      <main className={c('page')}>
        <Routes>
          <Route
            path="/embed/applications/:applicationID/*"
            element={
              <RequireAuth>
                <SuspensionBanner />
                <Embed />
              </RequireAuth>
            }
          />

          <Route
            path="/applications/:applicationID/*"
            element={
              <RequireAuth>
                <SuspensionBanner />
                <AppContainer />
              </RequireAuth>
            }
          />

          <Route
            path="*"
            element={
              <RequireAuth>
                <Navigate to={`/applications/${nextAppId}/overview`} />
              </RequireAuth>
            }
          />
        </Routes>
      </main>

      <MCPFrontendTools />
    </div>
  );
}
